import React, { useState } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import CustomModal from "../../common/general/CustomModal/CustomModal";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { UnChecked, Checked } from "../../svgs/common";
import { useAppConfig } from "../../../AppConfigProvider";

interface LessonPlanSection {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

interface GenerateLessonPlanModalProps {
  visible: boolean;
  onClose: () => void;
  onGenerate: (selectedSections: string[]) => void;
  isGenerating?: boolean;
  moduleTitle?: string;
}

const GenerateLessonPlanModal: React.FC<GenerateLessonPlanModalProps> = ({
  visible,
  onClose,
  onGenerate,
  isGenerating = false,
  moduleTitle = "Module 1: Design",
}) => {
  const { primaryColor } = useAppConfig();

  const [sections, setSections] = useState<LessonPlanSection[]>([
    {
      id: "objectives",
      title: "Lesson Objectives",
      description: "What students should understand and internalize by the end of this lesson.",
      enabled: true,
    },
    {
      id: "hook",
      title: "Hook / Introduction",
      description: "A short story, question, or visual to spark curiosity and draw students in.",
      enabled: true,
    },
    {
      id: "materials",
      title: "Materials Needed",
      description: "Suggested resources like readings, visuals, tools, or prompts to support the lesson.",
      enabled: true,
    },
    {
      id: "grammar",
      title: "Grammar Stage",
      description: "Foundational knowledge and key facts introduced clearly and systematically.",
      enabled: true,
    },
    {
      id: "logic",
      title: "Logic Stage",
      description: "Guided analysis and discussion that help students question and connect ideas.",
      enabled: true,
    },
    {
      id: "rhetoric",
      title: "Rhetoric Stage",
      description: "Opportunities for students to express and defend their understanding with clarity.",
      enabled: true,
    },
    {
      id: "activities",
      title: "Activity Instructions",
      description: "Steps for meaningful, student-centered learning experiences tied to your goals.",
      enabled: true,
    },
    {
      id: "assignments",
      title: "Suggested Assignments",
      description: "Recommended ways for students to demonstrate mastery, creativity, or reflection.",
      enabled: true,
    },
    {
      id: "assessment",
      title: "Student Assessment",
      description: "Ideas for checking understanding and reinforcing learning outcomes.",
      enabled: true,
    },
    {
      id: "time",
      title: "Time Allocation",
      description: "A balanced breakdown of how to pace your lesson segments effectively.",
      enabled: true,
    },
    {
      id: "tips",
      title: "Master Teaching Tips",
      description: "Strategies for differentiation, classroom flow, and supporting deeper learning.",
      enabled: true,
    },
    {
      id: "closure",
      title: "Closure / Reflection",
      description: "A brief wrap-up or prompt to help students connect the lesson to the bigger picture.",
      enabled: true,
    },
  ]);

  const toggleSection = (id: string) => {
    setSections((prev) =>
      prev.map((section) => (section.id === id ? { ...section, enabled: !section.enabled } : section))
    );
  };

  const handleGenerate = () => {
    const selectedSections = sections.filter((section) => section.enabled).map((section) => section.id);
    onGenerate(selectedSections);
  };

  const handleCancel = () => {
    if (!isGenerating) {
      onClose();
    }
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={handleCancel}
      ariaLabel="Generate Lesson Plan Modal"
      showCloseButton={false}>
      <View style={styles.container}>
        <View style={styles.header}>
          <CustomText
            style={styles.title}
            text={`Generate Lesson Plan for ${moduleTitle}`}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Axio will generate a lesson plan based on your module's content and structure. You can choose which sections to include. If no activities or resources have been added yet, Axio will recommend a few to help you get started."
            size="m"
            style={styles.description}
            useTranslationText={false}
          />
        </View>

        <View style={styles.content}>
          <ScrollView style={styles.sectionsContainer} showsVerticalScrollIndicator={false}>
            {sections.map((section, index) => (
              <View key={section.id}>
                <View style={styles.sectionRow}>
                  <CustomButton
                    styleType="transparent"
                    role="checkbox"
                    aria-checked={section.enabled}
                    aria-label={`${section.title} checkbox`}
                    style={styles.checkboxButton}
                    leftIcon={section.enabled ? <Checked /> : <UnChecked />}
                    onPress={() => toggleSection(section.id)}
                    disabled={isGenerating}
                  />
                  <View style={styles.sectionContent}>
                    <CustomText
                      text={section.title}
                      weight="bold"
                      size="m"
                      style={styles.sectionTitle}
                      useTranslationText={false}
                    />
                    <CustomText
                      text={section.description}
                      size="s"
                      style={styles.sectionDescription}
                      useTranslationText={false}
                    />
                  </View>
                </View>
                {index < sections.length - 1 && <CustomSpacing type="vertical" size="m" />}
              </View>
            ))}
          </ScrollView>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.buttonContainer}>
            <CustomButton
              text="Cancel"
              onPress={handleCancel}
              styleType="primaryLight"
              size="m"
              style={styles.cancelButton}
              disabled={isGenerating}
            />
            <CustomButton
              text={isGenerating ? "Generating..." : "Generate"}
              onPress={handleGenerate}
              styleType="primary"
              size="m"
              disabled={isGenerating || sections.filter((s) => s.enabled).length === 0}
              style={isGenerating ? { backgroundColor: "#ccc" } : {}}
            />
          </View>
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "hidden",
    width: "100%",
    flex: 1,
    height: "100%",
  },
  header: {
    flexDirection: "column",
    justifyContent: "flex-start",
    padding: 32,
    backgroundColor: "#F2F4F7",
    width: "100%",
    alignSelf: "stretch",
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    color: "#344054",
  },
  content: {
    padding: 24,
    flex: 1,
  },
  description: {
    color: "#667085",
    lineHeight: 24,
    fontSize: 16,
    fontWeight: "500",
  },
  sectionsContainer: {
    flex: 1,
  },
  sectionRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  checkboxButton: {
    padding: 0,
    marginRight: 12,
    marginTop: 2,
  },
  sectionContent: {
    flex: 1,
  },
  sectionTitle: {
    color: "#111827",
    marginBottom: 4,
  },
  sectionDescription: {
    color: "#6B7280",
    lineHeight: 18,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 2,
    borderColor: "#3E68FE",
    borderRadius: 8,
    paddingHorizontal: 16,
  },
});

export default GenerateLessonPlanModal;
